import { useMemo, useState } from 'react'

import { useQuery } from '@apollo/client'
import {
  Box,
  Skeleton,
  Text,
  WizardStep,
  WizardStepRenderParams,
  useOnNext
} from '@factorialco/deprecated-design-system'
import { F0Alert } from '@factorialco/f0-react'
import { EntityId, EntitySelectEntity } from '@factorialco/f0-react/dist/experimental'

import { TrainingsWithClassesDocument } from 'generated/resources/graphql'

import { EntitySelectWrapper } from 'modules/core/components/EntitySelectWrapper'
import { flatSelection } from 'modules/core/components/EntitySelectWrapper/lib/flat'
import i18n from 'modules/core/lib/i18n'

import {
  TrainingsWizardData,
  TrainingsWizardDataField,
  validateMandatoryFields
} from 'modules/finance/components/TrainingsBudgetWizard/lib/trainingsBudgetWizard'

import { BudgetSummaryCards } from './BudgetSummaryCards'
import {
  calculateAllocationCents,
  getSelectedEntities,
  mapTrainingsToEntities
} from './lib/trainingEntityMapper'
import { InfoCircle } from '@factorialco/f0-react/icons/app'

const AllocationStep = ({ data, onDataChanged }: WizardStepRenderParams<TrainingsWizardData>) => {
  const [expandedElements, setExpandedElements] = useState<EntityId[]>([])

  const { data: trainingsData, loading } = useQuery(TrainingsWithClassesDocument, {})
  const trainings = useMemo(
    () =>
      (trainingsData?.trainings?.trainingsConnection?.nodes ?? []).filter(
        (node): node is NonNullable<typeof node> => node !== null
      ),
    [trainingsData]
  )

  // Transform trainings to EntitySelectEntity format
  const entities = useMemo(
    () => mapTrainingsToEntities(trainings, expandedElements),
    [trainings, expandedElements]
  )

  // Get currently selected entities for display
  const selectedEntities = useMemo(
    () => getSelectedEntities(entities, data.selectedTrainingClassIds),
    [entities, data.selectedTrainingClassIds]
  )

  // Budget calculations
  const totalBudgetCents = useMemo(() => {
    const amount = parseFloat(data.amountCents) || 0
    return Math.round(amount * 100)
  }, [data.amountCents])

  const allocationCents = useMemo(
    () => calculateAllocationCents(trainings, data.selectedTrainingClassIds),
    [trainings, data.selectedTrainingClassIds]
  )

  const availableCents = totalBudgetCents - allocationCents

  // Handle multi-select from EntitySelectWrapper
  const handleSelectMultiple = (selection: EntitySelectEntity[] | null) => {
    if (!selection) {
      onDataChanged({
        ...data,
        selectedTrainingClassIds: [],
        selectedTrainingIds: [],
        errors: { ...data.errors, selectedTrainingIds: '' }
      })
      return
    }

    // Extract class IDs from subItems using flatSelection utility
    const classIds = flatSelection(selection).map((item) => String(item.subId))

    // Extract training IDs from parent entities
    const trainingIds = selection.map((entity) => Number(entity.id))

    onDataChanged({
      ...data,
      selectedTrainingClassIds: classIds,
      selectedTrainingIds: trainingIds,
      errors: { ...data.errors, selectedTrainingIds: '' }
    })
  }

  const handleNext = async () => {
    // At least one training class must be selected
    const hasSelection = data.selectedTrainingClassIds.length > 0

    if (!hasSelection) {
      const mandatoryFields: TrainingsWizardDataField[] = ['selectedTrainingIds']
      const { dataWithErrors } = validateMandatoryFields(mandatoryFields, data)
      onDataChanged(dataWithErrors)
      return Promise.reject()
    }
  }

  useOnNext(handleNext)

  return (
    <Box gap="s24" flexDirection="column">
      {/* Step Title and Description */}
      <Box gap="s8" flexDirection="column">
        <Text.Normal size="200" weight="semibold">
          {i18n.t('budgets.forms.trainings_budget.allocation.title')}
        </Text.Normal>
        <Text.Normal>{i18n.t('budgets.forms.trainings_budget.allocation.description')}</Text.Normal>
      </Box>

      {/* Budget Summary Cards */}
      <BudgetSummaryCards
        totalBudgetCents={totalBudgetCents}
        allocationCents={allocationCents}
        availableCents={availableCents}
        currency={data.currency}
      />

      <Box gap="s8" flexDirection="column" maxWidth="s600">
        {loading ? (
          <Box flexDirection="column" gap="s8">
            <Skeleton height="s64" width="full" />
            <Skeleton height="s64" width="full" />
          </Box>
        ) : trainings.length === 0 ? (
          <Box padding="s24" alignItems="center">
            <Text.Normal color="grey800">
              {i18n.t('budgets.forms.trainings_budget.allocation.no_trainings')}
            </Text.Normal>
          </Box>
        ) : (
          <EntitySelectWrapper
            entities={entities}
            groups={[{ label: i18n.t('common.all'), value: 'all' }]}
            expandedElements={expandedElements}
            selectedGroup="all"
            selectedGroupedEntities={selectedEntities}
            loading={loading}
            multiple
            hiddenAvatar
            singleSelector={false}
            label={i18n.t('budgets.forms.trainings_budget.allocation.training_group')}
            placeholder={i18n.t('budgets.forms.trainings_budget.allocation.placeholder')}
            searchPlaceholder={i18n.t('common.search')}
            selectAllLabel={i18n.t('common.select_all')}
            clearLabel={i18n.t('common.clear')}
            selectedLabel={i18n.t('common.selected')}
            selectedItemsCopy={i18n.t(
              'budgets.forms.trainings_budget.allocation.trainings_selected'
            )}
            notFoundTitle={i18n.t('budgets.forms.trainings_budget.allocation.no_trainings')}
            notFoundSubtitle=""
            width="s600"
            onSelect={() => {}}
            onSelectMultiple={handleSelectMultiple}
            onGroupChange={() => {}}
            onSelectExpandedElements={setExpandedElements}
          />
        )}

        {data.errors?.selectedTrainingIds && (
          <Text.Normal size="200" color="radicalRed1000">
            {data.errors.selectedTrainingIds}
          </Text.Normal>
        )}
      </Box>

      <Box maxWidth="s600">
        <F0Alert
          variant="neutral"
          icon={InfoCircle}
          title={i18n.t('budgets.forms.trainings_budget.allocation.info_title')}
          description={i18n.t('budgets.forms.trainings_budget.allocation.info_description')}
        />
      </Box>
    </Box>
  )
}

export const allocationStep = (): WizardStep<TrainingsWizardData> => ({
  title: i18n.t('budgets.forms.trainings_budget.allocation.title'),
  nextText: i18n.t('common.submit'),
  render: (props) => <AllocationStep {...props} />,
  isCompleted: () => true
})
